import api from '../api';
import type {
  UpdateUserRequest,
  CreateUserRequest,
  CreateUserResponse,
  CreateAssociationsRequest,
} from '@ilteducation/customer-mgmt-types';
import { ref } from 'vue';

type Role = CreateAssociationsRequest['schoolAssociations'][number]['role'];

export const useEditUser = () => {
  const error = ref<null | unknown>(null);
  const isLoading = ref<boolean>(false);

  const createUser = async (data: CreateUserRequest): Promise<CreateUserResponse | null> => {
    error.value = null;
    isLoading.value = true;
    try {
      const response = await api.user.create(data);

      error.value = null;
      isLoading.value = false;
      return response.data;
    } catch (err: unknown) {
      console.error(err);
      error.value = err;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const updateUser = async (userUid: string, data: Omit<UpdateUserRequest, 'userId'>): Promise<boolean> => {
    error.value = null;
    isLoading.value = true;
    try {
      await api.user.patch(userUid, data);
    } catch (err: unknown) {
      console.error(err);
      error.value = err;
      return false;
    } finally {
      isLoading.value = false;
    }

    error.value = null;
    return true;
  };

  const updateAssociation = async (userUid: string, schoolUnitId: string, role: Role): Promise<boolean> => {
    error.value = null;
    isLoading.value = true;

    try {
      isLoading.value = true;
      await api.user.updateAssociation(userUid, schoolUnitId, role);
    } catch (err: unknown) {
      console.error(err);
      error.value = err;
      isLoading.value = false;
      return false;
    }

    error.value = null;
    isLoading.value = false;
    return true;
  };

  const deleteAssociation = async (userUid: string, schoolUnitId: string): Promise<boolean> => {
    error.value = null;
    isLoading.value = true;

    try {
      isLoading.value = true;
      await api.user.deleteAssociation(userUid, schoolUnitId);
    } catch (err: unknown) {
      console.error(err);
      error.value = err;
      isLoading.value = false;
      return false;
    }

    error.value = null;
    isLoading.value = false;
    return true;
  };

  const upsertAssociations = async (
    userUid: string,
    associations: { schoolUid: string; role: Role }[],
  ): Promise<boolean> => {
    error.value = null;
    isLoading.value = true;

    try {
      isLoading.value = true;
      await api.user.upsertAssociations(userUid, associations);
    } catch (err: unknown) {
      console.error(err);
      error.value = err;
      isLoading.value = false;
      return false;
    }

    error.value = null;
    isLoading.value = false;
    return true;
  };

  const deleteMembership = async (uid: string, groupUid: string): Promise<boolean> => {
    error.value = null;
    isLoading.value = true;
    try {
      await api.user.deleteMembership(uid, groupUid);
    } catch (err) {
      console.error(err);
      error.value = err;
      return false;
    } finally {
      isLoading.value = false;
    }

    return true;
  };

  return {
    createUser,
    updateUser,
    updateAssociation,
    upsertAssociations,
    deleteAssociation,
    deleteMembership,
    isLoading,
    error,
  };
};
